import createTxpDataRow from './createTxpDataRow';
import { isSameDay, userDate } from 'owa-datetime';
import ActivityType from '../../schema/innerSchema/ActivityType';
import CardItem from '../../schema/viewSchema/CardItem';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import ParcelDelivery from '../../schema/ParcelDelivery';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpStrings from '../../schema/configSchema/TxpStrings';

/**
 * Extracts the Parcel Delivery specific data needed for displaying the card
 */

export default function createParcelDeliveryCard(
    parcelDeliveryEntity: ParcelDelivery,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons
): CardItem {
    let partOfOrder = parcelDeliveryEntity.partOfOrder;

    // format lines for Summary
    let seller = partOfOrder.seller ? partOfOrder.seller.name : null;
    let orderNumber = partOfOrder ? partOfOrder.orderNumber : null;
    let summaryFirstLine = txpStrings.formatters.format(
        txpStrings.strings.parcelDeliveryFirstLine,
        seller
    );
    let summarySecondLine = txpStrings.formatters.format(
        txpStrings.strings.parcelDeliverySecondLine,
        orderNumber
    );

    // format the estimate time
    let latestDeliveryDate = userDate(parcelDeliveryEntity.expectedArrivalUntil);
    const latestDeliveryDateString: string = latestDeliveryDate.toString();
    let earliestDeliveryDate = userDate(parcelDeliveryEntity.expectedArrivalFrom);
    let deliveryText: string;
    if (latestDeliveryDate) {
        if (earliestDeliveryDate && !isSameDay(earliestDeliveryDate, latestDeliveryDate)) {
            deliveryText = txpStrings.formatters.format(
                txpStrings.strings.deliveryTextRange,
                txpStrings.formatters.formatWeekDayDate(earliestDeliveryDate.toString()),
                txpStrings.formatters.formatWeekDayDate(latestDeliveryDateString)
            );
        } else {
            deliveryText = txpStrings.formatters.formatWeekDayDate(latestDeliveryDateString);
        }
    }

    // format lines for Details
    let trackingNumber = parcelDeliveryEntity.trackingNumber;
    let provider = parcelDeliveryEntity.provider ? parcelDeliveryEntity.provider.name : null;
    let orderedItem = partOfOrder ? partOfOrder.orderedItem : null;
    let itemText = partOfOrder && orderedItem && orderedItem.name ? orderedItem.name : null;
    let trackingText =
        trackingNumber && provider
            ? txpStrings.formatters.format(
                  txpStrings.strings.trackingText,
                  provider,
                  trackingNumber
              )
            : null;
    let address = parcelDeliveryEntity.deliveryAddress;
    let addressText = address && address.streetAddress ? address.streetAddress : null;
    addressText = showLocation ? addressText : null;
    const actionButtonData = parcelDeliveryEntity.trackingUrl
        ? {
              actionIconName: detailsIcons.deliveryTruck,
              actionUrl: parcelDeliveryEntity.trackingUrl,
              actionButtonText: txpStrings.strings.trackPackageButtonText,
              actionName: ActionNameConstants.TrackPackage,
          }
        : null;

    return {
        id: activityId,
        entityImage: entityImage,
        entityIcon: entityIcon,
        activityType: ActivityType.ParcelDelivery,
        entityType: EntityType.ParcelDelivery,
        summaryData: {
            firstLine: { text: summaryFirstLine },
            secondLine: { text: summarySecondLine },
        },
        detailsData: {
            firstLine: createTxpDataRow(itemText, null, entityIcon), // Need clarity from designer on the icon to use
            secondLine: createTxpDataRow(trackingText, null, detailsIcons.deliveryTruck), // Need clarity from designer on the icon to use
            thirdLine: createTxpDataRow(
                deliveryText,
                txpStrings.strings.deliveryText,
                detailsIcons.clock
            ),
            fourthLine: createTxpDataRow(
                addressText,
                null,
                detailsIcons.location,
                false /* is required */,
                null /* placeholder */,
                true /* includeToolTip */
            ),
        },
        brandsSummaryFirstLine: summaryFirstLine,
        brandsSummarySecondLine: txpStrings.formatters.format(
            txpStrings.strings.brandSummaryPackageSecondLineText,
            txpStrings.formatters.formatWeekDayDate(latestDeliveryDateString)
        ),
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
    } as CardItem;
}
